import type { EligibleProduct } from "../../../types";

import { useProductOfferStore } from "../../../stores";

/**
 * GAIA checklist/cue detections (e.g. a TRANSITION phrase heard, a device cue) carry no product
 * identifier — they only say "this behavior type happened in the call." This resolves which
 * product's checklist state a detection like that should be written to: the selected/focused
 * product, falling back to the first primary product when none is selected. Kept as a single seam
 * so attribution rules can change here without touching the store or any checklist UI component.
 *
 * Broadcasting to *every* primary product was tried and rejected: partners like Cricket routinely
 * have 2+ primary products eligible in one session (CRICKETHOMEPLUS, Cricket_Trade_In_Program), so
 * a detection made while pitching one would falsely check off the same section on the other —
 * reproducing the exact cross-product leak this attribution step exists to prevent, and putting a
 * false-positive checkmark on a product the agent never actually said anything about. Returns an
 * array (0 or 1 items) so call sites can loop over it uniformly.
 *
 * Known trade-off: if a detection arrives before eligibility has populated any product for the
 * session, this returns `[]` and the detection is dropped rather than buffered — there is no product
 * bucket to attribute it to yet, and writing it to a session-wide bucket would reintroduce the
 * cross-product leak this attribution step exists to prevent. See useSalesChecklistEventListener.test.ts
 * / useTechCuesListener.test.tsx for the covered behavior.
 */
export const getChecklistAttributionTargets = (sessionId: string): EligibleProduct[] => {
    const { getSelectedProduct, getPrimaryProducts } = useProductOfferStore.getState();
    const target = getSelectedProduct(sessionId) ?? getPrimaryProducts(sessionId).at(0);
    return target ? [target] : [];
};
